/**
 * PURPOSE: Reglas puras de la pestaña Cómputo (FTR-0066): puntos en vivo, validación del puesto, borrador de una
 *   disciplina, búsqueda/agrupación de disciplinas, textos y errores del sobre V2.
 * RESPONSIBILITIES:
 *   - Puntos con la tabla `scoring` del payload (puesto 1..6 → places[].points, otro puesto → otherPlacesPoints,
 *     + participationPoints si la participación está marcada). Solo vista previa: el backend recalcula al guardar.
 *   - Puesto: vacío o entero 1..máx(N, último puesto con puntos de `scoring`, hoy 6), N = instituciones inscritas: con
 *     menos de 6 inscritas se puede cargar hasta el 6.º (2026-10-18). Repetidos y huecos PERMITIDOS (no se validan).
 *   - Borrador por institución { place: texto, participation }, si cambió y el cuerpo del PUT (todas las inscritas).
 *   - Búsqueda sin tildes por nombre o sigla y grupos "Colectivas" / "Individuales" (campo `kind`).
 *   - Leyenda de puntos, fecha es-PE (hora de Lima), enlace público de Hayllis 2.0 y lectura del error del backend.
 * COLLABORATORS: ComputoGeneralArea.jsx, ComputoDisciplineList.jsx, ComputoDisciplineEditor.jsx,
 *   ComputoRankingPreview.jsx.
 * ROUTE: winscore/src/area/ComputoGeneralArea/computoGeneral.utils.js
 */

/** Hayllis 2.0 público (único lugar del área; winscore-web no tiene otra base de Hayllis 2.0). */
export const HAYLLIS_PUBLIC_BASE_URL = "https://hayllisports.com";

/** Página pública del cómputo; Hayllis toma el id antes del primer guion, así que el id basta. */
export const buildHayllisComputoUrl = (idevent) => `${HAYLLIS_PUBLIC_BASE_URL}/es/events/${Number(idevent)}/computo-general`;

const asInteger = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : 0;
};

/** Puntos por puesto (null = sin puesto = 0). */
export const pointsForPlace = (scoring, place) => {
  if (place === null || place === undefined) return 0;
  const entry = (scoring?.places || []).find((item) => Number(item.place) === Number(place));
  return entry ? asInteger(entry.points) : asInteger(scoring?.otherPlacesPoints);
};

/** { placePoints, participationPoints, points } de una fila. */
export const computeRowPoints = (scoring, { place, participation }) => {
  const placePoints = pointsForPlace(scoring, place);
  const participationPoints = participation ? asInteger(scoring?.participationPoints) : 0;
  return { placePoints, participationPoints, points: placePoints + participationPoints };
};

/**
 * Puesto máximo de una disciplina: las inscritas, pero nunca menos que el último puesto con puntos de `scoring` (6.º),
 * igual que el backend (maxPlaceFor). Sin `scoring`, solo las inscritas.
 */
export const maxPlaceFor = (participants, scoring) => {
  const lastScoringPlace = (scoring?.places || []).reduce((max, item) => Math.max(max, asInteger(item?.place)), 0);
  return Math.max(asInteger(participants), lastScoringPlace);
};

/**
 * Texto del input "Puesto" → { value, valid }. Vacío = sin puesto (válido). Si no, solo un entero entre 1 y max;
 * cualquier otra cosa (0, max+1, decimales, letras, signos) es inválida.
 */
export const parsePlaceInput = (text, max) => {
  const trimmed = String(text ?? "").trim();
  if (trimmed === "") return { value: null, valid: true };
  if (!/^\d+$/.test(trimmed)) return { value: null, valid: false };
  const place = Number(trimmed);
  return Number.isInteger(place) && place >= 1 && place <= max ? { value: place, valid: true } : { value: null, valid: false };
};

/** Borrador inicial: { [idinstitution]: { place: "3" | "", participation } }. */
export const buildDraft = (institutions = []) =>
  Object.fromEntries(
    institutions.map((institution) => [
      String(institution.id),
      {
        place: institution.place === null || institution.place === undefined ? "" : String(institution.place),
        participation: institution.participation !== false,
      },
    ])
  );

const draftRowOf = (draft, institution) =>
  draft?.[String(institution.id)] || { place: "", participation: institution.participation !== false };

/** Filas del editor con su estado: puesto interpretado, validez, si cambió y puntos en vivo. */
export const buildEditorRows = ({ institutions = [], draft, scoring }) => {
  const max = maxPlaceFor(institutions.length, scoring);
  return institutions.map((institution) => {
    const row = draftRowOf(draft, institution);
    const parsed = parsePlaceInput(row.place, max);
    const participation = row.participation !== false;
    const changed =
      !parsed.valid ||
      parsed.value !== (institution.place ?? null) ||
      participation !== (institution.participation !== false);
    return {
      institution,
      placeText: row.place,
      place: parsed.value,
      valid: parsed.valid,
      participation,
      changed,
      ...computeRowPoints(scoring, { place: parsed.valid ? parsed.value : null, participation }),
    };
  });
};

export const summarizeEditorRows = (rows) =>
  rows.reduce(
    (acc, row) => ({
      placePoints: acc.placePoints + row.placePoints,
      participationPoints: acc.participationPoints + row.participationPoints,
      points: acc.points + row.points,
      invalid: acc.invalid + (row.valid ? 0 : 1),
      changed: acc.changed + (row.changed ? 1 : 0),
    }),
    { placePoints: 0, participationPoints: 0, points: 0, invalid: 0, changed: 0 }
  );

/** Cuerpo del PUT: TODAS las instituciones inscritas (reemplazo completo de la disciplina). */
export const buildSaveEntries = (rows) =>
  rows.map((row) => ({
    idinstitution: Number(row.institution.id),
    place: row.valid ? row.place : null,
    participation: row.participation,
  }));

/** "1.º 10 · 2.º 8 · … · 7.º+ 0 · participación +2". */
export const formatScoringLegend = (scoring) => {
  const places = [...(scoring?.places || [])].sort((a, b) => Number(a.place) - Number(b.place));
  const parts = places.map((item) => `${item.place}.º ${item.points}`);
  const lastPlace = places.length ? Number(places[places.length - 1].place) : 0;
  parts.push(`${lastPlace + 1}.º+ ${asInteger(scoring?.otherPlacesPoints)}`);
  parts.push(`participación +${asInteger(scoring?.participationPoints)}`);
  return parts.join(" · ");
};

export const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** Filtra por nombre o sigla, sin tildes ni mayúsculas. */
export const filterDisciplines = (disciplines = [], query = "") => {
  const needle = normalizeSearchText(query);
  if (!needle) return disciplines;
  return disciplines.filter((discipline) =>
    normalizeSearchText([discipline.label, discipline.acronym].filter(Boolean).join(" ")).includes(needle)
  );
};

/** Grupos en el orden en que llegan las disciplinas (el backend ya las ordena). */
export const groupDisciplines = (disciplines = []) =>
  [
    { key: "collective", label: "Colectivas", items: disciplines.filter((item) => item.kind === "collective") },
    { key: "individual", label: "Individuales", items: disciplines.filter((item) => item.kind !== "collective") },
  ].filter((group) => group.items.length > 0);

/** "6 puestos · 22 inscritas" (singular cuando corresponde). */
export const formatDisciplineCounts = (discipline) => {
  const places = asInteger(discipline?.placesAssigned);
  const participants = asInteger(discipline?.participants);
  return `${places} ${places === 1 ? "puesto" : "puestos"} · ${participants} ${participants === 1 ? "inscrita" : "inscritas"}`;
};

/** Fecha y hora en es-PE, hora de Lima. */
export const formatComputoDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("es-PE", {
    timeZone: "America/Lima",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** "15/10/2026, 19:31 · usuario" o "" si nunca se editó. */
export const formatLastUpdate = (lastUpdate) => {
  if (!lastUpdate?.at) return "";
  const when = formatComputoDate(lastUpdate.at);
  const who = lastUpdate.by?.username;
  return [when, who].filter(Boolean).join(" · ");
};

const FALLBACK_MESSAGES = Object.freeze({
  SCHEMA_NOT_READY: "Falta aplicar la actualización de base de datos del Cómputo General.",
  COMPUTO_DISABLED: "El Cómputo General no está habilitado para este evento. Actívalo en Ajustes.",
});

/** Error de axios → { status, code, message, details } con el mensaje del backend cuando lo hay. */
export const readComputoError = (error, fallback = "No se pudo completar la operación del Cómputo General.") => {
  const body = error?.response?.data;
  const code = typeof body?.code === "string" ? body.code : null;
  const message =
    (typeof body?.message === "string" && body.message) || (code && FALLBACK_MESSAGES[code]) || fallback;
  return {
    status: Number(error?.response?.status) || null,
    code,
    message,
    details: body?.details && typeof body.details === "object" ? body.details : null,
  };
};
